import { TestId } from '@modules/utilities/constants/testConstants';
import { useTranslation } from '@rbx/intl';
import React, { useMemo } from 'react';
import { makeStyles, Typography } from '@rbx/ui';
import { stickyBreadcrumbScrollOffset } from '@modules/ui/shared/utils/styles';
import {
  APIReferenceMember,
  MemberType,
  MemberTypeListNameTranslKey,
  ReferenceType,
} from '@modules/client';
import { summaryID } from '../../constants/apiReferenceConstants';
import { EnumReference, ParentClassReference, Reference } from '../../types';
import {
  getReferenceSummaryMemberTableInheritedMembers,
  getReferenceSummaryMemberTableMembers,
} from '../../utils/apiMemberUtils';
import { orderedMemberTypes } from '../../utils/apiReferenceUtils';
import ReferenceSummaryMemberTableV2 from './ReferenceSummaryMemberTableV2';
import ReferenceInheritedMembers from './ReferenceInheritedMembers';
import ReferenceInheritedBy from './ReferenceInheritedBy';

type InheritedGroup = {
  parent: string;
  total: number;
  membersByType: Record<MemberType, APIReferenceMember[]>;
};

function buildInheritedGroups(
  orderedParentReferences: ParentClassReference[] | null | undefined,
  referenceType: ReferenceType,
): InheritedGroup[] {
  if (!orderedParentReferences || !orderedParentReferences.length) return [];

  const memberTypes = orderedMemberTypes(referenceType);
  const parentNames = orderedParentReferences.map((p) => p.name);
  const parentMap: Record<string, InheritedGroup> = {};

  memberTypes.forEach((memberType) => {
    const inheritedForType = getReferenceSummaryMemberTableInheritedMembers(
      orderedParentReferences,
      memberType,
    );

    parentNames.forEach((parentName) => {
      const list = (inheritedForType[parentName] || []) as APIReferenceMember[];
      if (list && list.length > 0) {
        const group =
          parentMap[parentName] ||
          ({ parent: parentName, total: 0, membersByType: {} } as InheritedGroup);
        group.membersByType[memberType] = list;
        group.total += list.length;
        parentMap[parentName] = group;
      }
    });
  });

  return parentNames.map((name) => parentMap[name]).filter((g): g is InheritedGroup => Boolean(g));
}

const useStyles = makeStyles()(() => ({
  container: {
    marginBottom: '24px',
  },
  header: {
    marginBottom: '24px',
    scrollMarginTop: stickyBreadcrumbScrollOffset,
  },
}));

interface ReferenceSummaryV2Props {
  referenceType: ReferenceType;
  reference: Exclude<Reference, EnumReference>;
  orderedParentReferences?: ParentClassReference[] | null;
}

/**
 * V2 Summary section for engine revamp - displays member tables with new styling
 */
export default function ReferenceSummaryV2({
  referenceType,
  reference,
  orderedParentReferences,
}: ReferenceSummaryV2Props) {
  const { translate } = useTranslation();
  const { classes } = useStyles();

  const tables = useMemo(() => {
    return orderedMemberTypes(referenceType)
      .map((memberType) => ({
        memberType,
        title: translate(MemberTypeListNameTranslKey[memberType]) || memberType,
        members: getReferenceSummaryMemberTableMembers(reference, memberType) || [],
        isEventMemberType: memberType === MemberType.Event,
      }))
      .filter(({ members }) => members.length > 0);
  }, [referenceType, reference, translate]);

  const inheritedGroups = useMemo<InheritedGroup[]>(() => {
    return buildInheritedGroups(orderedParentReferences, referenceType);
  }, [orderedParentReferences, referenceType]);

  return (
    <div data-testid={TestId.ApiReferenceSummary} className={classes.container}>
      <div id={summaryID} className={classes.header}>
        <Typography variant='h2'>{translate('Label.Summary') || 'Summary'}</Typography>
      </div>
      {tables.map(({ memberType, title, members, isEventMemberType }) => (
        <ReferenceSummaryMemberTableV2
          key={title}
          memberType={memberType}
          title={title}
          ariaLabel={title}
          referenceName={reference.name}
          referenceType={referenceType}
          members={members as APIReferenceMember[]}
          isEventMemberType={isEventMemberType}
        />
      ))}
      {/* Inherited members section (grouped by parent, collapsible) */}
      {inheritedGroups.length > 0 && (
        <ReferenceInheritedMembers referenceType={referenceType} groups={inheritedGroups} />
      )}
      {/* Descendants section */}
      {'descendants' in reference && reference.descendants && reference.descendants.length > 0 && (
        <ReferenceInheritedBy referenceType={referenceType} descendants={reference.descendants} />
      )}
    </div>
  );
}
